import { useState } from 'react'
import { Lock, ArrowUpCircle, Sparkles, AlertTriangle } from 'lucide-react'
import { useScopeStore } from '../store'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { ResellerLockBadge, useIsResellerLocked, useHasResellerParent } from './ResellerLock'
import { useUpsellContext } from '../hooks/useUpsellContext'
import { ScopeSuggestModal } from './ScopeSuggestModal'

interface GeneralInfoTabProps {
  dealId: number | null
}

export function GeneralInfoTab({ dealId }: GeneralInfoTabProps) {
  const { scope, updateGeneralInfo, updateKycConfig, updateAmlConfig, updateBiometricsConfig, updateKybConfig } = useScopeStore()
  const [showSuggest, setShowSuggest] = useState(false)
  const { generalInfo, kybConfig } = scope
  const disabled = scope.finalized
  const resellerImported = useHasResellerParent()
  const technicalSetupLocked = resellerImported || scope.finalized
  const integrationTypeLocked = useIsResellerLocked('generalInfo', 'integrationType')
  const platformVersionLocked = useIsResellerLocked('generalInfo', 'platformVersion')
  const { isUpsell, incompleteClass, mapLabel } = useUpsellContext()

  const generalSection = getSectionById('general_pdf_export')

  const clientUseCaseField = getFieldById('general_client_use_case')

  const timelineVolumeGroup = getGroupById('general_timeline_volume')
  const productionAccessDateField = getFieldById('general_production_access_date')
  const goLiveDateField = getFieldById('general_go_live_date')
  const forecastChecks12mField = getFieldById('general_forecast_checks_12m')

  const technicalSetupGroup = getGroupById('general_technical_setup')
  const integrationTypeField = getFieldById('general_integration_type')
  const platformVersionField = getFieldById('general_platform_version')
  const ssoAccessField = getFieldById('general_sso_access')
  const aiFeaturesField = getFieldById('general_ai_features')

  const phasesGroup = getGroupById('general_phases_group')
  const phasesField = getFieldById('general_phases')

  // Completion checks for required fields — suppressed for upsells
  const isClientUseCaseIncomplete = !isUpsell && generalInfo.clientUseCase.trim() === ''
  const isProductionDateIncomplete = !isUpsell && generalInfo.productionAccessDate === ''
  const isGoLiveDateIncomplete = !isUpsell && generalInfo.goLiveDate === ''
  const isIntegrationTypeIncomplete = !isUpsell && generalInfo.integrationType === 'unknown'
  const isSsoAccessIncomplete = !isUpsell && generalInfo.ssoAccess === 'unknown'
  const isAiFeaturesIncomplete = !isUpsell && generalInfo.aiFeatures === 'unknown'

  // V2 feature availability warnings
  const V2_CUTOFF_DATE = '2026-08-31'
  const v2Features: { key: string; label: string }[] = []
  if (kybConfig?.internationalEnabled === 'yes') v2Features.push({ key: 'internationalKyb', label: 'International KYB' })
  if (kybConfig?.trustAnalyserEnabled === 'yes') v2Features.push({ key: 'trustAnalyser', label: 'Trust Analyser' })
  if (kybConfig?.onboardingFormEnabled === 'yes') v2Features.push({ key: 'onboardingForm', label: 'Business Onboarding Form' })
  if (kybConfig?.amlEntityScreening === 'yes') v2Features.push({ key: 'amlEntityScreening', label: 'Business AML' })
  const goLiveBeforeCutoff = generalInfo.goLiveDate && generalInfo.goLiveDate < V2_CUTOFF_DATE
  const showV2Warning = v2Features.length > 0 && goLiveBeforeCutoff

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {generalSection?.formTitle || 'General Information'}
        </h3>
        {dealId && !disabled && (
          <button
            onClick={() => setShowSuggest(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded-lg hover:bg-violet-500/20 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            AI Assist
          </button>
        )}
      </div>

      {/* Upsell Toggle */}
      <div className="flex items-center gap-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={generalInfo.isUpsell || false}
            onChange={(e) => {
              updateGeneralInfo('isUpsell', e.target.checked)
              if (!e.target.checked) updateGeneralInfo('scopeConfirmed', false)
            }}
            disabled={disabled}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
        </label>
        <div className="flex items-center gap-2">
          <ArrowUpCircle size={15} className={generalInfo.isUpsell ? 'text-primary' : 'text-[var(--text-muted)]'} />
          <span className={`text-sm font-medium ${generalInfo.isUpsell ? 'text-primary' : 'text-[var(--text-secondary)]'}`}>
            Upsell
          </span>
          {generalInfo.isUpsell && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary border border-primary/30">
              Existing Customer
            </span>
          )}
        </div>
      </div>

      <div className={isClientUseCaseIncomplete ? incompleteClass : ''}>
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
          {clientUseCaseField?.formLabel || 'Client Use Case'}
        </label>
        <textarea
          className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          placeholder={clientUseCaseField?.placeholder || 'Briefly describe the problem the client is solving with FrankieOne'}
          value={generalInfo.clientUseCase}
          onChange={(e) => {
            const value = e.target.value
            const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0
            if (wordCount <= 500) updateGeneralInfo('clientUseCase', value)
          }}
          disabled={disabled}
        />
        <p className="text-xs text-[var(--text-muted)] mt-1">
          <span className="text-primary font-bold mr-1">*</span>
          {clientUseCaseField?.formDescription || 'Required for completion. Up to 500 words. Keep it outcome-focused (what, why, and success criteria).'}
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-8">
        <div className="pt-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {timelineVolumeGroup?.formTitle || 'Timeline & Volume'}
          </h4>
        </div>

        <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Production Access Date */}
            <div className={isProductionDateIncomplete ? incompleteClass : ''}>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {productionAccessDateField?.formLabel || 'Production Access Date'}
              </label>
              <input
                type="date"
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.productionAccessDate}
                onChange={(e) => updateGeneralInfo('productionAccessDate', e.target.value)}
                disabled={disabled}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="text-primary font-bold mr-1">*</span>
                {productionAccessDateField?.formDescription ||
                  'Required for completion. Target date to receive FrankieOne Production keys.'}
              </p>
            </div>

            {/* Expected Go Live Date */}
            <div className={isGoLiveDateIncomplete ? incompleteClass : ''}>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {goLiveDateField?.formLabel || 'Expected Go Live Date'}
                {generalInfo.isHardGoLiveDate && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 border border-red-300">
                    Hard Date
                  </span>
                )}
              </label>
              <input
                type="date"
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.goLiveDate}
                onChange={(e) => updateGeneralInfo('goLiveDate', e.target.value)}
                disabled={disabled}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="text-primary font-bold mr-1">*</span>
                {goLiveDateField?.formDescription ||
                  'Required for completion. Target date the client expects to go live.'}
              </p>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generalInfo.isHardGoLiveDate || false}
                  onChange={(e) => updateGeneralInfo('isHardGoLiveDate', e.target.checked)}
                  disabled={disabled}
                  className="rounded border-[var(--border-color)] text-primary focus:ring-primary"
                />
                <span className="text-sm text-[var(--text-primary)]">
                  Hard date <span className="text-xs text-[var(--text-secondary)]">(firm client commitment)</span>
                </span>
              </label>
            </div>

            {/* Forecast KYC/AML Checks */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {forecastChecks12mField?.formLabel || 'Forecast KYC/AML Checks (12 months)'}
              </label>
              <input
                type="number"
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder={forecastChecks12mField?.placeholder || 'e.g., 10000'}
                value={generalInfo.forecastChecks12m ?? ''}
                onChange={(e) => updateGeneralInfo('forecastChecks12m', e.target.value ? Number(e.target.value) : null)}
                disabled={disabled}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {forecastChecks12mField?.formDescription || 'Estimated number of checks expected in the next 12 months.'}
              </p>
            </div>
          </div>

          {showV2Warning && (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-400">V2 Features Not Available at Go-Live</p>
                  <p className="text-xs text-amber-300/80 mt-1">
                    The following features are scoped but will not be available for implementation before {V2_CUTOFF_DATE}:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {v2Features.map((f) => (
                      <li key={f.key} className="text-xs text-amber-300/80 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                        {f.label}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-300/70 mt-2 italic">
                    These features will be excluded from the initial go-live implementation and delivered in a subsequent phase once available.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pt-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {technicalSetupGroup?.formTitle || 'Technical Setup'}
          </h4>
          {resellerImported && (
            <p className="text-xs text-blue-300 mt-1 flex items-center gap-1">
              <Lock size={11} /> Inherited from reseller — read-only
            </p>
          )}
        </div>

        <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Integration Type */}
            <div className={isIntegrationTypeIncomplete ? incompleteClass : ''}>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {integrationTypeField?.formLabel || 'Integration Type'}
                <ResellerLockBadge section="generalInfo" field="integrationType" />
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.integrationType}
                onChange={(e) => updateGeneralInfo('integrationType', e.target.value as any)}
                disabled={disabled || integrationTypeLocked || technicalSetupLocked}
              >
                {(integrationTypeField?.options || [
                  { value: 'unknown', label: "Don't Know" },
                  { value: 'api', label: 'API' },
                  { value: 'portal', label: 'Portal Only' },
                  { value: 'both', label: 'Both' },
                ]).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {mapLabel(opt.label)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="text-primary font-bold mr-1">*</span>
                {integrationTypeField?.formDescription ||
                  'Required for completion. How the client will integrate with FrankieOne (API, Portal, or both).'}
              </p>
            </div>

            {/* Platform Version */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {platformVersionField?.formLabel || 'Platform Version'}
                <ResellerLockBadge section="generalInfo" field="platformVersion" />
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.platformVersion}
                onChange={(e) => updateGeneralInfo('platformVersion', e.target.value as any)}
                disabled={disabled || platformVersionLocked || technicalSetupLocked}
              >
                {(platformVersionField?.options || [
                  { value: 'unknown', label: "Don't Know" },
                  { value: 'v1', label: 'V1' },
                  { value: 'v2', label: 'V2' },
                ]).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {mapLabel(opt.label)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {platformVersionField?.formDescription || 'Which FrankieOne platform version will be used.'}
              </p>
            </div>

            {/* SSO Access */}
            <div className={isSsoAccessIncomplete ? incompleteClass : ''}>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {ssoAccessField?.formLabel || 'SSO Access'}
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.ssoAccess}
                onChange={(e) => updateGeneralInfo('ssoAccess', e.target.value as any)}
                disabled={disabled || technicalSetupLocked}
              >
                {(ssoAccessField?.options || [
                  { value: 'unknown', label: "Don't Know" },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {mapLabel(opt.label)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="text-primary font-bold mr-1">*</span>
                {ssoAccessField?.formDescription ||
                  'Required for completion. Whether the client requires SSO access for the FrankieOne portal.'}
              </p>
            </div>

            {/* AI Features */}
            <div className={isAiFeaturesIncomplete ? incompleteClass : ''}>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {aiFeaturesField?.formLabel || 'AI Features'}
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.aiFeatures}
                onChange={(e) => updateGeneralInfo('aiFeatures', e.target.value as any)}
                disabled={disabled || technicalSetupLocked}
              >
                {(aiFeaturesField?.options || [
                  { value: 'unknown', label: "Don't Know" },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {mapLabel(opt.label)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="text-primary font-bold mr-1">*</span>
                {aiFeaturesField?.formDescription ||
                  'Required for completion. Whether AI features should be disabled for this account.'}
              </p>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {phasesGroup?.formTitle || phasesField?.formLabel || 'Phases'}
          </h4>
        </div>

        <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className="text-xs text-[var(--text-muted)]">
            {phasesGroup?.formDescription ||
              'Phase 1 always belongs to this deal. Future phases may be moved to a new deal.'}
          </div>

          {generalInfo.phases.map((phaseText, idx) => {
            const isPhaseIncomplete = !isUpsell && phaseText.trim() === ''
            return (
              <div key={idx} className={isPhaseIncomplete ? incompleteClass : ''}>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Phase {idx + 1} Scope</label>
                <textarea
                  className="w-full min-h-28 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
                  placeholder={phasesField?.placeholder || 'Describe what is in scope for this phase'}
                  value={phaseText}
                  onChange={(e) => {
                    const value = e.target.value
                    const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0
                    if (wordCount <= 500) {
                      const next = [...generalInfo.phases]
                      next[idx] = value
                      updateGeneralInfo('phases', next)
                    }
                  }}
                  disabled={disabled}
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="text-primary font-bold mr-1">*</span>
                  {phasesField?.formDescription || 'Required for completion. Up to 500 words.'}
                </p>
              </div>
            )
          })}

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              onClick={() => {
                if (generalInfo.phases.length >= 5) return
                updateGeneralInfo('phases', [...generalInfo.phases, ''])
              }}
              disabled={disabled || generalInfo.phases.length >= 5}
            >
              Add Phase
            </button>

            <button
              type="button"
              className="px-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
              onClick={() => {
                if (generalInfo.phases.length <= 1) return
                updateGeneralInfo('phases', generalInfo.phases.slice(0, -1))
              }}
              disabled={disabled || generalInfo.phases.length <= 1}
            >
              Remove Last Phase
            </button>

            <span className="text-xs text-[var(--text-muted)]">{generalInfo.phases.length}/5</span>
          </div>
        </div>

      </div>

      <ScopeSuggestModal
        dealId={dealId}
        isOpen={showSuggest}
        onClose={() => setShowSuggest(false)}
        onApply={(section, field, value) => {
          if (section === 'general') {
            if (field === 'phases' && Array.isArray(value)) {
              updateGeneralInfo('phases', value as string[])
            } else {
              updateGeneralInfo(field as any, value as any)
            }
          } else if (section === 'kyc') {
            updateKycConfig(field as any, value as any)
          } else if (section === 'aml') {
            updateAmlConfig(field as any, value as any)
          } else if (section === 'biometrics') {
            if ((field === 'documentVerification' || field === 'selfieVerification') && value === 'yes') {
              updateBiometricsConfig('enabled', 'yes')
            }
          } else if (section === 'kyb') {
            updateKybConfig(field as any, value as any)
          }
        }}
      />
    </div>
  )
}
